<template>
  <component
    :is="href ? 'a' : 'span'"
    class="pc-cta-btn"
    :href="href || null"
    :target="href ? '_blank' : null"
    :rel="href ? 'noopener noreferrer' : null"
    :aria-disabled="href ? null : 'true'"
  >
    <span class="pc-cta-btn__wipe" aria-hidden="true" />
    <span class="pc-cta-btn__label">{{ label }}</span>
    <svg
      class="pc-cta-btn__chevron"
      viewBox="0 0 28 47"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0 0 L16 23.5 L0 47 L12 47 L28 23.5 L12 0 Z" fill="currentColor" />
    </svg>
  </component>
</template>

<script>
/**
 * PC 通用 CTA 按钮。
 *
 * 外形沿用首页巡演卡片的 list-item-btn（.pc-index__about-wrap-list-item-btn，
 * 切图 index/card_btn_*.webp）：左上 / 右下大圆角的胶囊、右侧折线箭头、
 * 右下错位副色块。切图把文案烤死在图里，需要自定义文字时用本 CSS 还原版本。
 * 比例取自切图（720x120）：左上圆角 0.67h、右下圆角 0.79h、箭头 28x47 且右边距 0.10w。
 *
 * hover / active 沿用 .pc-index__ticket-wrap-content-more-stack：
 * scale(1.04) + 黄色辉光，并用 clip-path 从左往右揭出 hover 配色
 * （对应那边 more_title_1 → more_title_3 的切图换色）。
 *
 * 注意：不能提到 views/pc 之外——vue.config.js 的 postcss 按 `/views/pc/`
 * 路径才挂 pxtorem，移出目录后 px 不再随屏缩放。
 * 移动端对应 views/mobile/components/MobileCtaButton.vue。
 */
export default {
  name: "PcCtaButton",
  props: {
    label: {
      type: String,
      required: true,
    },
    /** 为空时渲染为不可点击的 span（链接待运营填入） */
    href: {
      type: String,
      default: "",
    },
  },
};
</script>

<style lang="scss" scoped>
.pc-cta-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 180px;
  max-width: 100%;
  height: 30px;
  padding: 0 30px;
  text-decoration: none;
  color: #191616;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.04em;
  cursor: pointer;
  transform-origin: center center;
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
    filter 0.2s ease;

  /**
   * 三层同框叠色，靠 border-radius 差异露出错位块：
   * ::before 黄色垫层（右下角圆角很小）→ ::after 紫色主体（右下角大圆角，于是右下露出黄月牙）
   * → __wipe hover 配色（默认整块裁掉）。
   * 伪元素在树序里是首尾两端，必须显式写 z-index 才能让 __wipe 压在 ::after 之上。
   */
  &::before,
  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }

  &::before {
    z-index: 0;
    background-color: #fff380;
    border-radius: 20px 0 4px 0;
  }

  &::after {
    z-index: 1;
    background-color: #d9a2ff;
    border-radius: 20px 0 24px 0;
  }

  /* 揭色与右下角错位块同为 #fff380：揭满后错位块自然融进底色（同 more_title_3 的处理） */
  &__wipe {
    position: absolute;
    inset: 0;
    z-index: 2;
    background-color: #fff380;
    border-radius: 20px 0 24px 0;
    clip-path: inset(0 0 0 100%);
    pointer-events: none;
    transition: clip-path 0.45s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &__label,
  &__chevron {
    position: relative;
    z-index: 3;
  }

  &__label {
    white-space: nowrap;
  }

  &__chevron {
    position: absolute;
    top: 50%;
    right: 18px;
    width: 7px;
    height: 12px;
    transform: translateY(-50%);
  }

  &:hover,
  &:focus-visible {
    transform: scale(1.04);
    filter: brightness(1.1) drop-shadow(0 6px 18px rgba(255, 243, 128, 0.25));
  }

  &:hover .pc-cta-btn__wipe,
  &:focus-visible .pc-cta-btn__wipe {
    clip-path: inset(0 0 0 0);
  }

  &:active {
    transform: scale(0.96);
    filter: brightness(0.9);
    transition-duration: 0.08s;
  }

  &:focus-visible {
    outline: 2px solid #fff380;
    outline-offset: 6px;
  }

  /* 链接未配置时不给交互反馈 */
  &[aria-disabled="true"] {
    cursor: default;

    &:hover {
      transform: none;
      filter: none;
    }

    &:hover .pc-cta-btn__wipe {
      clip-path: inset(0 0 0 100%);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .pc-cta-btn {
    transition: none;

    &:hover,
    &:focus-visible,
    &:active {
      transform: none;
      filter: none;
    }

    .pc-cta-btn__wipe {
      transition: none;
    }
  }
}
</style>
